<template>
  <v-form
    ref="form"
    v-model="isFormValid"
  >
    <v-form-base
      v-if="isSchemaLoaded"
      :value="udf.model"
      :schema="udf.schema"
      @input="onInput"
    />
  </v-form>
</template>

<script>
import getFormConfig from '@/util/formUtility.js'
import commonService from '@/service/commonService.js'
import VFormBase from 'dco-vuetify-form'
import { rearrangeSchemaBySequence } from '@/composables/useSchemaUtils'

export default {
  name: 'UdfFormBuilder',
  components: {
    VFormBase
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    formConfig: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      udf: {
        model: {},
        schema: {}
      },
      isFormValid: false,
      isSchemaLoaded: false,
      originalItems: {},
      originalRules: {},
      dependencyMap: {},
      cachedGenericCodes: {}
    }
  },
  watch: {
    // Reset form when value prop changes to null/undefined
    value (newVal) {
      if (newVal == null && this.isSchemaLoaded) {
        // Reset model to empty values
        Object.keys(this.udf.model).forEach(key => {
          this.udf.model[key] = ''
        })

        // Reinitialize dependent field states
        this.initializeDependentFieldStates()

        // Reset form validation
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.resetValidation()
          }
        })
      }
    },
    isFormValid (newVal) {
      this.$emit('form-validity-change', newVal)
    }
  },
  async created () {
    // Step 1: Load form configuration from JSON
    const formConfig = getFormConfig(
      await commonService.getFormData(this.formConfig)
    )

    // Step 2: Filter out excluded fields from schema and model
    const filteredSchema = Object.fromEntries(
      Object.entries(formConfig.schema).filter(([_, config]) => !config.excluded)
    )
    const filteredModel = Object.fromEntries(
      Object.entries(formConfig.model).filter(([key]) => !formConfig.schema[key]?.excluded)
    )

    // Step 3: Prepare model with merged values
    const model = {
      ...filteredModel,
      ...this.value
    }

    // Step 4: Prepare schema with rearrangement
    const schema = rearrangeSchemaBySequence({ schema: filteredSchema }).schema

    // Step 5: Get all gcReferenceKeys from the schema
    const codeReferences = Object.values(schema)
      .filter(config => config.gcReferenceKey && config.gcReferenceKey !== '$PARENT_VALUE')
      .map(config => ({ reference: config.gcReferenceKey }))

    // Step 6: Get all reference codes and populate schema items
    const { aggregateCodes = [] } = await commonService.getGenericCodes({ codeReferences })
    if (aggregateCodes.length > 0) {
      // Sort codes inside each reference by codeDescription
      aggregateCodes.forEach(ref => {
        if (Array.isArray(ref.codes)) {
          ref.codes.sort((a, b) => a.codeDescription.localeCompare(b.codeDescription))
        }
      })

      Object.values(schema)
        .filter(config => config.gcReferenceKey)
        .forEach(config => {
          const reference = aggregateCodes.find(ref => ref.reference === config.gcReferenceKey)
          if (!reference) return
          config.items = reference.codes
        })
    }

    // Step 7: Build dependency map and initialize dependent fields state
    Object.entries(schema).forEach(([field, config]) => {
      if (!config?.dependsOn) { return }

      // Build dependency map: map parent field to its dependent children
      const parentField = config.dependsOn.parentField
      if (parentField) {
        if (!this.dependencyMap[parentField]) {
          this.dependencyMap[parentField] = []
        }
        this.dependencyMap[parentField].push(field)
      }

      // Store original rules for later use
      if (config.rules) {
        this.originalRules[field] = config.rules
        config.rules = []
      }

      // Handle cascading dropdowns with itemsMap
      if (config.items) {
        this.originalItems[field] = Object.fromEntries(
          config.items.map(item => [item.codeReference, item])
        )
        config.items = []
      }

      // Handle conditional visibility with whenEquals or whenNotEquals - evaluate initial state
      if ('whenEquals' in config.dependsOn || 'whenNotEquals' in config.dependsOn) {
        const parentValue = model[config.dependsOn.parentField]
        let isMatch = false

        if ('whenEquals' in config.dependsOn) {
          // whenEquals: Check if parent value matches specific values
          const whenEquals = config.dependsOn.whenEquals
          isMatch = Array.isArray(whenEquals)
            ? whenEquals.includes(parentValue)
            : whenEquals === parentValue
        } else if ('whenNotEquals' in config.dependsOn) {
          // whenNotEquals: Check if parent value does NOT match specific values
          const whenNotEquals = config.dependsOn.whenNotEquals
          isMatch = Array.isArray(whenNotEquals)
            ? !whenNotEquals.includes(parentValue)
            : whenNotEquals !== parentValue
        }

        this.applyBehaviorState(config, field, isMatch ? 'onMatch' : 'onNoMatch')
      }
    })

    // Step 8: Assign to reactive properties once all processing is complete
    this.udf.model = model
    this.udf.schema = schema
    this.isSchemaLoaded = true
  },
  methods: {
    // Handle cascading dropdowns (itemsMap) and clear field value when needed
    processItemsMap (config, field, newValue) {
      let fieldValueChanged = false
      if (config.dependsOn?.itemsMap) {
        if (newValue) {
          const itemsMap = config.dependsOn.itemsMap[newValue]
          config.items = itemsMap && itemsMap.length > 0
            ? itemsMap.map(codeRef => this.originalItems[field][codeRef])
            : []
        } else {
          config.items = []
        }

        if (this.udf.model[field] !== '') {
          this.udf.model[field] = ''
          fieldValueChanged = true
        }
      }
      return fieldValueChanged
    },

    // Load generic codes when gcReferenceKey === '$PARENT_VALUE' and set items
    async loadParentValueCodes (config, newValue) {
      if (config.gcReferenceKey !== '$PARENT_VALUE') { return }
      if (!this.cachedGenericCodes[newValue]) {
        const codeReferences = [{ reference: newValue }]
        const { aggregateCodes = [] } = await commonService.getGenericCodes({ codeReferences })
        if (aggregateCodes.length > 0) {
          const codes = aggregateCodes.find(ref => ref.reference === newValue)?.codes || []
          codes.sort((a, b) => a.codeDescription.localeCompare(b.codeDescription))
          this.cachedGenericCodes[newValue] = codes
        } else {
          this.cachedGenericCodes[newValue] = []
        }
      }
      config.items = this.cachedGenericCodes[newValue]
    },

    // Evaluate conditional visibility and apply behavior; returns if field value was cleared
    applyConditionalVisibility (config, field, newValue) {
      let fieldValueChanged = false
      if ('whenEquals' in config.dependsOn || 'whenNotEquals' in config.dependsOn) {
        let isMatch = false

        if ('whenEquals' in config.dependsOn) {
          const whenEquals = config.dependsOn.whenEquals
          isMatch = Array.isArray(whenEquals)
            ? whenEquals.includes(newValue)
            : whenEquals === newValue
        } else if ('whenNotEquals' in config.dependsOn) {
          const whenNotEquals = config.dependsOn.whenNotEquals
          isMatch = Array.isArray(whenNotEquals)
            ? !whenNotEquals.includes(newValue)
            : whenNotEquals !== newValue
        }

        this.applyBehaviorState(config, field, isMatch ? 'onMatch' : 'onNoMatch')

        if (!isMatch && this.udf.model[field] !== '') {
          this.udf.model[field] = ''
          fieldValueChanged = true
        }
      }
      return fieldValueChanged
    },

    initializeDependentFieldStates () {
      // Reinitialize dependent fields state (same as Step 7 in created)
      Object.entries(this.udf.schema).forEach(([field, config]) => {
        if (!config?.dependsOn) { return }

        // Restore original rules or clear them
        if (this.originalRules[field]) {
          config.rules = []
        }

        // Reset cascading dropdowns to empty
        if (this.originalItems[field]) {
          config.items = []
        }

        // Apply onNoMatch behavior for conditional visibility
        if ('whenEquals' in config.dependsOn || 'whenNotEquals' in config.dependsOn) {
          this.applyBehaviorState(config, field, 'onNoMatch')
        }
      })
    },
    applyBehaviorState (config, field, behaviorKey) {
      const behavior = config.dependsOn?.behavior?.[behaviorKey]
      if (!behavior) { return }

      Object.entries(behavior).forEach(([prop, value]) => {
        if (prop === 'applyRules') {
          // Handle rules flag - restore or clear rules based on flag
          config.rules = value && this.originalRules[field] ? this.originalRules[field] : []
        } else {
          // apply other properties like hidden, disabled
          config[prop] = value
        }
      })
    },
    updateDependentFields (changedField, newValue, updatedFields = new Set()) {
      // Prevent infinite loops by tracking which fields have been updated
      if (updatedFields.has(changedField)) { return }

      updatedFields.add(changedField)

      // Get direct dependents from pre-built map (O(1) lookup instead of O(n) iteration)
      const dependentFields = this.dependencyMap[changedField]
      if (!dependentFields || dependentFields.length === 0) { return }

      // Process each dependent field sequentially to avoid race conditions
      (async () => {
        for (const field of dependentFields) {
          const config = this.udf.schema[field]

          // Items map processing
          let fieldValueChanged = this.processItemsMap(config, field, newValue)

          // Dynamic generic codes ($PARENT_VALUE)
          await this.loadParentValueCodes(config, newValue)

          // Conditional visibility (whenEquals/whenNotEquals)
          fieldValueChanged = this.applyConditionalVisibility(config, field, newValue) || fieldValueChanged

          // Recursively update fields that depend on this field
          if (fieldValueChanged) {
            this.updateDependentFields(field, this.udf.model[field], updatedFields)
          }
        }
      })()
    },
    onInput (e) {
      const { key, value } = e

      // Normalize null to empty string to ensure consistent empty value handling
      const normalizedValue = value === null ? '' : value

      this.updateDependentFields(key, normalizedValue)
      this.udf.model[key] = normalizedValue
      this.$emit('input', this.udf.model)
    },
    /**
     * Public method: Reset form validation state
     * Called by parent components via $refs
     * @example this.$refs.udfFormBuilder.resetValidation()
     * @public
     */
    // eslint-disable-next-line vue/no-unused-properties
    resetValidation () {
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    }
  }
}
</script>
